import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardBody, CardHeader, Row, Col } from 'reactstrap';
import CompanyContext from '../../../auth/CompanyContext';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import Loading from '../../Loading/Loading';

ChartJS.register(ArcElement, Tooltip, Legend);

const initialChartData = {
    labels: [],
    holdings: [],
    datasets: [{
        label: 'Market Value',
        data: [],
        backgroundColor: [],
        percentage: []
    }]
};

const PortalAccountOverview = (props) => {
    const companyCtx = useContext(CompanyContext);
    const hasCompanyPortalDatabase = String(props.companySettings?.CompanyPortalDatabase || '').trim() !== '';

    const { callback, onWidgetLoad, onAddCallback } = props;

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            }
        }
    }), []);

    const getValues = useCallback((width) => {
        let col1 = 0, col2 = 0, graphHeight = 0;
        switch (width) {
            case 4:
                col1 = 6; col2 = 6; graphHeight = 350;
                break;
            case 6:
                col1 = 6; col2 = 6; graphHeight = 350;
                break;
            case 12:
                col1 = 3; col2 = 3; graphHeight = 300;
                break;
            default:
        }
        return { col1, col2, graphHeight };
    }, []);

    const initialValues = getValues(props.width);
    const [data, setData] = useState(initialChartData);
    const [selectedDate, setSelectedDate] = useState(false);
    const [selectedDateObject, setSelectedDateObject] = useState(null);
    const [labels, setLabels] = useState([]);
    const [col1, setCol1] = useState(initialValues.col1);
    const [col2, setCol2] = useState(initialValues.col2);
    const [graphHeight, setGraphHeight] = useState(initialValues.graphHeight);
    const [assetAllocation, setAssetAllocation] = useState('');
    const [sector, setSector] = useState('');
    const [classification, setClassification] = useState('securityName');
    const [dataState, setDataState] = useState({ sort: [{ field: 'sector', dir: 'asc' }] });
    const [initialLoad, setInitialLoad] = useState(true);
    const [loading, setLoading] = useState(true);
    const [plugins, setPlugins] = useState([]);
    const [dates, setDates] = useState([]);
    const [result, setResult] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [styleSettings, setStyleSettings] = useState({});
    const [type, setType] = useState(null);
    const [animation, setAnimation] = useState(false);

    const refreshRef = useRef(() => { });
    const classificationRef = useRef('securityName');
    const dataStateRef = useRef({ sort: [{ field: 'sector', dir: 'asc' }] });
    const initialLoadRef = useRef(true);

    const getClassificationGroupingString = useCallback((stringIn = '') => {
        let output = stringIn || 'Securities';
        output = output.replace(/Asset Class/g, 'assetAllocation');
        output = output.replace(/Sector/g, 'sector');
        output = output.replace(/Securities/g, 'securityName');
        output = output.replace(/ > /g, ',');
        return output;
    }, []);

    const sortByPercentageDesc = useCallback((a, b) => {
        const aPercentage = Number(a && a.percentage) || 0;
        const bPercentage = Number(b && b.percentage) || 0;
        if (bPercentage !== aPercentage) return bPercentage - aPercentage;

        const aMarketValue = Math.abs(Number(a && a.marketValue) || 0);
        const bMarketValue = Math.abs(Number(b && b.marketValue) || 0);
        return bMarketValue - aMarketValue;
    }, []);

    const processData = useCallback((dataIn, dataStateIn = {}) => {
        let processed = Array.isArray(dataIn) ? [...dataIn] : [];
        const sort = Array.isArray(dataStateIn.sort) ? dataStateIn.sort : [];

        if (sort.length > 0) {
            processed.sort((a, b) => {
                for (let i = 0; i < sort.length; i++) {
                    const { field, dir = 'asc' } = sort[i];
                    const aValue = a ? a[field] : undefined;
                    const bValue = b ? b[field] : undefined;

                    if (aValue === bValue) continue;
                    if (aValue == null) return 1;
                    if (bValue == null) return -1;
                    if (aValue > bValue) return dir === 'desc' ? -1 : 1;
                    if (aValue < bValue) return dir === 'desc' ? 1 : -1;
                }
                return 0;
            });
        }

        const totalRows = processed.length;
        const skip = Number.isInteger(dataStateIn.skip) ? dataStateIn.skip : 0;
        const take = Number.isInteger(dataStateIn.take) ? dataStateIn.take : null;
        if (take != null) processed = processed.slice(skip, skip + take);
        else if (skip > 0) processed = processed.slice(skip);

        return { data: processed, total: totalRows };
    }, []);

    const aggregateByFields = useCallback((dataIn, descriptors = []) => {
        return descriptors.reduce((acc, descriptor) => {
            if (!descriptor || !descriptor.field || !descriptor.aggregate) return acc;
            const { field, aggregate } = descriptor;
            if (!acc[field]) acc[field] = {};
            if (aggregate === 'sum') {
                acc[field].sum = dataIn.reduce((sum, item) => sum + (Number(item && item[field]) || 0), 0);
            }
            return acc;
        }, {});
    }, []);

    const getDataStructure = useCallback((dataIn = []) => {
        const nextLabels = [];
        const percentages = [];
        const values = [];
        const colorsIn = [];
        const holdingsIn = [];

        for (let i = 0; i < dataIn.length; i++) {
            const item = dataIn[i] || {};
            const color = item.color || 'rgb(128, 128, 128)';
            nextLabels.push(item.label);
            percentages.push(item.percentage);
            values.push(item.marketValue);
            colorsIn.push(color);
            holdingsIn.push({
                label: item.label,
                percentage: item.percentage,
                marketValue: item.marketValue,
                color
            });
        }

        setLabels(nextLabels);

        return {
            labels: nextLabels,
            holdings: holdingsIn,
            datasets: [{
                label: 'Market Value',
                data: values,
                backgroundColor: colorsIn,
                percentage: percentages
            }]
        };
    }, []);

    const formatCash = useCallback((num) => {
        if (num < 1e3) {
            return num.toFixed(2);
        }
        if (num >= 1e3 && num < 1e6) {
            if ((num / 1e3) < 10) return +(Math.floor((num * 100) / 1e3) / 100).toFixed(2) + ' K';
            if ((num / 1e3) >= 10 && (num / 1e3) < 100) return +(Math.floor((num * 10) / 1e3) / 10).toFixed(1) + ' K';
            return +(Math.floor(num / 1e3)).toFixed(0) + ' K';
        }
        if (num >= 1e6 && num < 1e9) {
            if ((num / 1e6) < 10) return +(Math.floor((num * 100) / 1e6) / 100).toFixed(2) + ' M';
            if ((num / 1e6) >= 10 && (num / 1e6) < 100) return +(Math.floor((num * 10) / 1e6) / 10).toFixed(1) + ' M';
            return +(Math.floor(num / 1e6)).toFixed(0) + ' M';
        }
        if (num >= 1e9 && num < 1e12) {
            if ((num / 1e9) < 10) return +(Math.floor((num * 100) / 1e9) / 100).toFixed(2) + ' B';
            if ((num / 1e9) >= 10 && (num / 1e9) < 100) return +(Math.floor((num * 10) / 1e9) / 10).toFixed(1) + ' B';
            return +(Math.floor(num / 1e9)).toFixed(0) + ' B';
        }
        if ((num / 1e12) < 10) return +(Math.floor((num * 100) / 1e12) / 100).toFixed(2) + ' T';
        if ((num / 1e12) >= 10 && (num / 1e12) < 100) return +(Math.floor((num * 10) / 1e12) / 10).toFixed(1) + ' T';
        return +(Math.floor(num / 1e12)).toFixed(0) + ' T';
    }, []);

    const setTotalMarketValue = useCallback((num) => {
        const formattedMarketValueTotal = parseFloat(num) ? formatCash(parseFloat(num)) : 'N/A';

        setPlugins([{
            id: 'centerText',
            beforeDraw: function (chart) {
                const width = chart.width;
                const height = chart.height;
                const ctx = chart.ctx;
                ctx.restore();
                const fontSize = (((width / 160) + (height / 200)) / 2).toFixed(2);
                ctx.font = 'bold ' + fontSize + 'em sans-serif';
                ctx.textBaseline = 'middle';
                const text = formattedMarketValueTotal;
                const textX = Math.round((width - ctx.measureText(text).width) / 2);
                const textY = height / 2;
                ctx.fillText(text, textX, textY);
                ctx.save();
            }
        }]);
    }, [formatCash]);

    const getStyleSettings = useCallback(async () => {
        if (!hasCompanyPortalDatabase) return;

        const styleNames = ['acctOverviewClassificationGrouping'];
        const params = new URLSearchParams();
        styleNames.forEach(name => params.append('styleNames[]', name));

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/getStyleSettings?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();
            const nextStyleSettings = response.styleSettings || {};
            const classificationGrouping = getClassificationGroupingString(nextStyleSettings.acctOverviewClassificationGrouping || 'Securities');
            const groupingsArray = classificationGrouping.split(',');
            const classificationValue = groupingsArray[0] || 'securityName';

            classificationRef.current = classificationValue;
            setClassification(classificationValue);
            setStyleSettings(nextStyleSettings);
        } catch (err) {
            console.error(err);
        }
    }, [companyCtx.company, props.idToken, getClassificationGroupingString, hasCompanyPortalDatabase]);

    const getHoldDates = useCallback(async () => {
        if (!hasCompanyPortalDatabase) {
            setDates([]);
            return null;
        }

        const payload = {
            port: '',
            household: props.selectedHousehold
        };

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/posDates`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                },
                body: JSON.stringify(payload)
            });
            const response = await results.json();
            if (response.code === 200 && response.data && response.data.length > 0) {
                const nextSelectedDate = response.data[0].fDate;
                setSelectedDate(nextSelectedDate);
                setSelectedDateObject(new Date(nextSelectedDate));
                setDates(response.data || []);
                return nextSelectedDate;
            }
            setDates([]);
            return null;
        } catch (err) {
            console.log(err);
            return null;
        }
    }, [props.selectedHousehold, props.idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const getAssetAllocationData = useCallback(async (selectedDateValue) => {
        if (!hasCompanyPortalDatabase) {
            setLoading(false);
            setType(null);
            setData({ ...initialChartData });
            setTotalMarketValue(0);
            return;
        }

        const sectorValue = '';
        const assetAllocationValue = '';
        const nextType = null;

        const port = props.selectedHousehold;
        const params = new URLSearchParams({
            date: selectedDateValue || selectedDate || '',
            classification: classificationRef.current || classification,
            household: port,
            includeAccruedInterest: true
        });

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/assetAllocationWidget?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();

            if (response.code !== 200) {
                setLoading(false);
                setType(nextType);
                alert('Could not retrieve asset class data.');
                return;
            }

            let filteredData = Array.isArray(response.data) ? response.data.filter(() => true) : [];
            filteredData.sort(sortByPercentageDesc);

            let resData = [...filteredData];
            const dataStateCopy = {
                ...dataStateRef.current,
                sort: [{ field: 'percentage', dir: 'desc' }]
            };
            dataStateRef.current = dataStateCopy;
            setDataState(dataStateCopy);

            let chartDataIn = [];
            let totalMarketValue = '';
            let processedResult = { data: [], total: 0 };

            if (resData.length > 0) {
                processedResult = processData(resData, dataStateCopy);
                chartDataIn = [...processedResult.data];
                const totals = aggregateByFields(processedResult.data, [{ field: 'marketValue', aggregate: 'sum' }, { field: 'percentage', aggregate: 'sum' }]);
                processedResult = {
                    ...processedResult,
                    data: [
                        ...processedResult.data,
                        {
                            assetAllocation: 'TOTALS',
                            sector: 'TOTALS',
                            securityName: 'TOTALS',
                            securitySymbol: 'TOTALS',
                            marketValue: totals.marketValue.sum
                        }
                    ]
                };
                totalMarketValue = totals.marketValue.sum;
            }

            const structuredData = getDataStructure(filteredData);
            setTotalMarketValue(totalMarketValue);

            if (initialLoadRef.current) {
                initialLoadRef.current = false;
                setInitialLoad(false);
            }

            setData(structuredData);
            setResult(processedResult);
            setChartData(chartDataIn);
            setLoading(false);
            setAnimation(true);
            setSector(sectorValue);
            setAssetAllocation(assetAllocationValue);
            setType(nextType);
        } catch (err) {
            console.log(err);
            setLoading(false);
            setType(nextType);
        }
    }, [props.selectedHousehold, props.idToken, companyCtx.company, selectedDate, classification, sortByPercentageDesc, processData, aggregateByFields, getDataStructure, setTotalMarketValue, hasCompanyPortalDatabase]);

    const onSubmit = useCallback(async () => {
        setLoading(true);
        const selectedDateValue = await getHoldDates();
        getAssetAllocationData(selectedDateValue);
    }, [getHoldDates, getAssetAllocationData]);

    const runRefresh = useCallback(() => {
        onSubmit();
    }, [onSubmit]);

    useEffect(() => {
        refreshRef.current = runRefresh;
    }, [runRefresh]);

    useEffect(() => {
        let active = true;

        const init = async () => {
            await getStyleSettings();
            if (!active) return;

            callback((width) => {
                if (!active) return;
                const values = getValues(width);
                setGraphHeight(values.graphHeight);
                setCol1(values.col1);
                setCol2(values.col2);
            });

            if (onWidgetLoad) {
                onWidgetLoad();
                onAddCallback(() => {
                    refreshRef.current();
                });
            }
        };

        init();

        return () => {
            active = false;
        };
    }, [hasCompanyPortalDatabase]);

    void labels;
    void assetAllocation;
    void sector;
    void classification;
    void dataState;
    void initialLoad;
    void dates;
    void result;
    void chartData;
    void styleSettings;
    void type;
    void animation;

    return (
        <Card className='w-100 m-0'>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <h4>Account Overview</h4>
                {props.icon}
            </CardHeader>
            <CardBody className='p-3'>
                {loading && <Loading />}
                {!loading &&
                    <Row className="mb-4">
                        <Col md={col1}>
                            {(!loading && data.labels.length !== 0) ?
                                <div style={{ height: graphHeight + 'px' }}>
                                    <Doughnut
                                        data={data}
                                        options={chartOptions}
                                        plugins={plugins}
                                    />
                                </div>
                                : <></>}
                        </Col>
                        <Col md={col2}>
                            <div className='acctOverviewTopHoldings'>
                                {(!loading && data.labels.length !== 0) && selectedDateObject &&
                                    <div className='acctOverviewSnapshotHeader'><label>{`Top Holdings as of ${selectedDateObject.toLocaleDateString('en-US', { timeZone: 'UTC' })}`}</label></div>
                                }
                                {(!loading && data.labels.length !== 0) &&
                                    <table className='acctOverviewSnapshot'>
                                        <tbody>
                                            {(data.holdings || []).slice(0, 5).map((holding, i) => (
                                                <Fragment key={i}>
                                                    <tr>
                                                        <td className='acctOverviewColor' style={{ backgroundColor: holding.color }} onMouseEnter={() => { }} onMouseLeave={() => { }}>
                                                        </td>
                                                        <td className='acctOverviewLabel'>
                                                            <label>{holding.label}</label>
                                                        </td>
                                                        <td className='acctOverviewPercent'>
                                                            <label>{(Math.round(holding.percentage * 10000) / 100).toFixed(2)}%</label>
                                                        </td>
                                                    </tr>
                                                    <tr className='acctOverviewValueRow'>
                                                        <td />
                                                        <td />
                                                        <td className='acctOverviewValue'>
                                                            <label>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(holding.marketValue)}</label>
                                                        </td>
                                                    </tr>
                                                    <tr style={{ height: '8px' }}></tr>
                                                </Fragment>
                                            ))}
                                        </tbody>
                                    </table>}
                            </div>
                        </Col>
                    </Row>}
                {!loading && data.labels.length === 0 &&
                    <div className='widgetNoData'>No data available</div>
                }
                {loading && <Loading />}
            </CardBody>
        </Card>
    );
};

export default PortalAccountOverview;
